/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'@waca/dashboard-common/js/core/APIFactory',
	'./DashboardPropertiesV2API',
	'../../../app/nls/StringResources',
	'mobx'
], function (APIFactory, DashboardPropertiesV2API, StringResources, Mobx) {

	const PARENT_PANEL_CONTAINER_ID = 'parentPanel';

	class DashboardPropertiesV2 {

		constructor({ features }) {
			this._api = APIFactory.createAPI(this, [DashboardPropertiesV2API]);
			this._dashboard = features.API;
			this._selectedContentIds = [];
			this._canvas = null;
			this._initContext();
		}

		initialize() {
			this._dashboard.getCanvasWhenReady().then((canvas) => {
				this._canvas = canvas;
				this._canvas.on('change:selections:select', this._updateSelectedContentId, this);
				this._canvas.on('change:selections:deselect', this._updateSelectedContentId, this);
			});
		}

		_initContext(){
			this._context = {
				activeContainer : PARENT_PANEL_CONTAINER_ID,
				containers: [
					{
						id: PARENT_PANEL_CONTAINER_ID,
						label: StringResources.get('dashboardProperties'),
						ancestors: []
					}]
			};
		}

		_updateSelectedContentId() {
			this._initContext();
			this._selectedContentIds = this.getContentIds();
		}

		destroy() {
			if (this._canvas) {
				this._canvas.off('change:selections:select', this._updateSelectedContentId, this);
				this._canvas.off('change:selections:deselect', this._updateSelectedContentId, this);
			}
		}

		getAPI() {
			return this._api;
		}

		getPropertyList() {
			return this.propertiesList;
		}

		_getPropertiesLayoutList() {
			return this.propertyLayoutList;
		}

		getPropertyLayoutList() {
			return this.expandedPropertyLayoutList;
		}

		getSelectedContentIds() {
			return this._selectedContentIds;
		}

		getContentIds() {
			const contents = this._canvas.getSelectedContentList();
			const contentIds = [];
			if (contents && contents.length) {
				for (const content of contents) {
					contentIds.push(content.getId());
				}
			}
			return contentIds;
		}

		/**
		 * gets the common property list for single/multiple selections when there is any selection;
		 * if property id is the same for multiple selections, we default to the value from the first selection.
		 * @example propertyList1 = [{id: 'a', value: 'a'}], propertyList2 = [{id: 'a', value: 'b'}]
		 * returns [[{id: 'a', value: 'a'}]]
		 *
		 * gets canvas property list (dashboard content and canvas content) when there is no selection;
		 */
		get propertiesList() {
			void (this._selectedContentIds);
			if(!this._canvas){
				return; // mobx auto-run can be invoked when canvas is not ready. return in this case
			}
			const contents = this._canvas.getSelectedContentList();
			if (contents.length) {
				const propertyList = [];
				const allContentsPropertyNameList = [];
				for (const content of contents) {
					const contentProperties = content.getPropertyList();
					const propertyIds = contentProperties.map(function (property) {
						return property.id;
					});
					allContentsPropertyNameList.push(propertyIds);
				}
				const intersectionNameList = allContentsPropertyNameList.reduce((a, b) => a.filter(c => b.includes(c)));
				for (const name of intersectionNameList) {
					const contentPropertyList = contents[0].getPropertyList();
					propertyList.push(contentPropertyList.find(property => property.id === name));
				}
				return propertyList;
			} else {
				return this._canvas.getPropertyList();
			}
		}

		get propertyLayoutList() {
			void (this._selectedContentIds);
			if(!this._canvas){
				return; // mobx auto-run can be invoked when canvas is not ready. return in this case
			}
			const hasSelection = this._canvas.getSelectedContentList().length;
			if (hasSelection) {
				const contents = this._canvas.getSelectedContentList();
				const tabItems = [];
				let bannerControl;
				for (const content of contents) {
					const contentLayoutProperties = content.getPropertyLayoutList();
					let tabs = contentLayoutProperties;
					if (!bannerControl) {
						const banner = contentLayoutProperties.find(function (layout) { return layout.type === 'Banner'; });
						bannerControl = banner;
					}
					tabs = tabs.filter((layout => layout.type !== 'Banner'));
					tabItems.unshift(tabs);
				}
				while (tabItems.length > 1) {
					tabItems[0] = this._mergePropertySpecs(tabItems[1], tabItems[0], true);
					tabItems.splice(1, 1);
				}
				const commonPropertyLayoutList = [];
				if (tabItems.length < 1) {
					return commonPropertyLayoutList;
				}
				commonPropertyLayoutList.push(bannerControl);
				commonPropertyLayoutList.push(...tabItems[0]);
				return commonPropertyLayoutList;
			} else {
				return this._getCanvasPropertyLayoutList();
			}
		}

		_mergePropertySpecs(spec1, spec2, onlyKeepCommonProperties) {
			if (!spec1) {
				return spec2;
			} else if (!spec2) {
				return spec1;
			}

			const result = onlyKeepCommonProperties ? [] : spec1;
			spec2.forEach((spec2Item) => {
				// If the property doesn't have an id then simply append it
				if (!spec2Item.id) {
					result.push(spec2Item);
				} else {
					let matchedItem = null;
					for (var i = 0; i < spec1.length; i++) {
						if (spec1[i].id === spec2Item.id) {
							matchedItem = spec1[i];
							break;
						}
					}
					if (matchedItem) {
						if (matchedItem.items && spec2Item.items) {
							// Recursively merge the child item arrays
							matchedItem.items = this._mergePropertySpecs(matchedItem.items, spec2Item.items, onlyKeepCommonProperties);
						}
						if (onlyKeepCommonProperties) {
							result.push(matchedItem);
						}
					} else if (!onlyKeepCommonProperties) {
						result.push(spec2Item);
					}
				}
			});
			return result;
		}

		_getCanvasPropertyLayoutList() {
			const dashboardContentPropertyLayoutList = this._canvas.getPropertyLayoutList() || [];
			const canvasRootContentPropertyLayoutList = this._canvas.getContent().getPropertyLayoutList() || [];
			const unionPropertyLayoutMap = {};
			for (const layout of dashboardContentPropertyLayoutList) {
				unionPropertyLayoutMap[layout.id] = layout;
			}
			for (const layout of canvasRootContentPropertyLayoutList) {
				if (unionPropertyLayoutMap[layout.id]) {
					unionPropertyLayoutMap[layout.id].items = [...unionPropertyLayoutMap[layout.id].items, ...layout.items]; // union
				} else {
					unionPropertyLayoutMap[layout.id] = layout;
				}
			}
			return Object.values(unionPropertyLayoutMap);
		}

		get expandedPropertyLayoutList() {
			void (this._selectedContentIds);
			/*expanded layout list contains the merge of layout list and metadata list*/
			const propertyLayoutList = this._getPropertiesLayoutList();
			return propertyLayoutList;
		}

		getContext(){
			return this._context;
		}

	}

	Mobx.decorate(DashboardPropertiesV2, {
		propertiesList: Mobx.computed,
		_selectedContentIds: Mobx.observable
	});

	return DashboardPropertiesV2;

});
